import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { parseTableParams, formatDate } from '../../../utils/Utils';
import DataTable from '../../../components/ui/DataTable';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';

export default function EmployeeAccessPermissionsTab({ employeeId }) {
  const { t, i18n } = useTranslation();
  const { canWrite } = useAuth();
  const lang = i18n.language;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;

    const allParams = { ...paramsToUse, 'userId[eq]': employeeId };
    setFetching(true);
    api.get(`/v1/access-permissions?${new URLSearchParams(allParams)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger, employeeId]);

  const handleNewButtonClick = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    try {
      await api.post('/v1/access-permissions/assign', {
        employeeIds: [employeeId],
        gateId: formData.gateId,
        validFrom: formData.validFrom,
        validTo: formData.validTo,
      });
      setRefreshTrigger((p) => p + 1);
      setDrawerOpen(false);
      return true;
    } catch {
      return false;
    }
  }, [employeeId]);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({
      isOpen: true,
      ids,
      name: `${ids.length} ${t('accessPermission.title') || 'record(s)'}`,
    });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    try {
      await api.post('/v1/access-permissions/batch-revoke', { ids: deleteConfirm.ids });
      setRefreshTrigger((p) => p + 1);
    } catch {
      // handled by interceptor
    } finally {
      setDeleting(false);
      setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    }
  }, [deleteConfirm.ids]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, columns);
    setQueryParams(params);
  }, []);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'gateName', label: t('accessPermission.gateName') || 'Gate', options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'gateGroupName', label: t('accessPermission.gateGroupName') || 'Gate group', options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'validFrom', label: t('accessPermission.validFrom') || 'Valid from',
      options: {
        filter: false, sort: true,
        customBodyRender: (v) => formatDate(v, lang),
      },
    },
    {
      name: 'validTo', label: t('accessPermission.validTo') || 'Valid to',
      options: {
        filter: false, sort: true,
        customBodyRender: (v) => formatDate(v, lang),
      },
    },
  ], [t, lang]);

  // GenericFormDrawer'ın `asyncSelect` alanı YALNIZCA `onSearch` fonksiyonunu okur.
  // Buradaki seçici eskiden `asyncConfig: { url, labelKey, valueKey }` veriyordu; bu
  // alan depoda hiçbir yerde okunmuyor, AsyncGenericSelect de undefined çağrısını
  // try/catch içinde yutuyordu → kapı listesi sessizce boş kalıyor, bu sekmeden
  // kapı yetkisi atanamıyordu.
  const searchGates = useCallback(async (query) => {
    const params = query ? { 'name[like]': query, pageSize: 20 } : { pageSize: 20 };
    try {
      const res = await api.get('/v1/gates', { params });
      const page = res.data?.data;
      const items = Array.isArray(page) ? page : (page?.data || []);
      return items.map((g) => ({ id: g.id, name: g.name }));
    } catch {
      return [];
    }
  }, []);

  const objectSchema = useMemo(() => [
    {
      name: 'gateId',
      label: t('accessPermission.gateName') || 'Gate',
      type: 'asyncSelect',
      grid: 12,
      required: true,
      onSearch: searchGates,
    },
    { name: 'validFrom', label: t('accessPermission.validFrom') || 'Valid from', type: 'date', grid: 6 },
    { name: 'validTo', label: t('accessPermission.validTo') || 'Valid to', type: 'date', grid: 6 },
  ], [t, searchGates]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('accessPermission.assign') || 'Assign'}
        schema={objectSchema}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('common.delete') || 'Delete'}
        message={t('accessPermission.revokeMessage', { name: deleteConfirm.name }) || `Revoke ${deleteConfirm.name}?`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      <DataTable
        title={t('employee.accessPermissions')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        canCreateNewRecord={canWrite('AC')}
        onNewButtonClick={handleNewButtonClick}
        onRowsDelete={canWrite('AC') ? handleRowsDelete : undefined}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={handleDataStateChange}
      />
    </Container>
  );
}

const Container = styled.div``;
